/**
 * Client für Partner Management (tri-server docs/partner-management.md): Partner-Mandanten eines
 * Master-Mandanten auflisten und anlegen. Löschen gibt es nicht.
 */
import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

export interface Partner {
  id: string;
  name: string;
  created: string | null;
  address: { street?: string; streetNumber?: string; postalCode?: string; city?: string } | null;
  users: number;
  admin: { email: string; name: string; invitationPending: boolean } | null;
  activeOffers: number;
}

export interface PartnerOffer {
  serviceReference: string;
  name: string;
  type: string;
  planName: string | null;
  validTo: string;
  source: "order" | "own";
}

export interface NewPartner {
  admin: { email: string; firstName: string; lastName: string; mobileNumber?: string };
  company: {
    name: string;
    tradeRegisterId?: string;
    tradeRegisterCity?: string;
    taxId?: string;
    street: string;
    streetNumber: string;
    postalCode: string;
    city: string;
    country?: string;
  };
}

const processResponse = async (response: Response) => {
  if (response.ok) return response.json();
  if (response.status === 401) throw new UnauthorizedError();
  let message = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    message = body.message || body.error || message;
  } catch {
    // kein JSON
  }
  throw new ApplicationError(message);
};

export const usePartnerAPI = () => {
  const base = configuration.serverUrl;
  const json = async (path: string, init: RequestInit = {}) =>
    processResponse(await authenticatedFetch(`${base}${path}`, { ...init, headers: { "Content-Type": "application/json" } }));
  return {
    partners: (customer: string): Promise<{ partners: Partner[] }> => json(`/customers/${encodeURIComponent(customer)}/partners`),
    offers: (customer: string): Promise<{ offers: PartnerOffer[] }> => json(`/customers/${encodeURIComponent(customer)}/partners/offers`),
    create: (customer: string, partner: NewPartner): Promise<{ id: string; name: string; invitationSent: boolean; offers: Array<{ name: string }> }> =>
      json(`/customers/${encodeURIComponent(customer)}/partners`, { method: "POST", body: JSON.stringify(partner) }),
  };
};
